import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsHome.css';

function formatCount(value) {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value || 0).toLocaleString('en-US');
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return '—';
  return `${Number(value || 0).toFixed(1)}%`;
}

function formatUsdFromCents(value) {
  if (value === null || value === undefined || value === '') return '—';
  return (Number(value || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatHours(value) {
  if (value === null || value === undefined || value === '') return '—';
  const hours = Number(value || 0);
  if (!Number.isFinite(hours)) return '—';
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

function formatDays(value) {
  if (value === null || value === undefined || value === '') return '—';
  const days = Number(value || 0);
  if (!Number.isFinite(days)) return '—';
  return `${days.toFixed(1)}d`;
}

function MetricCard({ title, value, caption }) {
  return (
    <article className="ops-card">
      <div className="ops-card-title">{title}</div>
      <div className="ops-card-value">{value}</div>
      <div className="ops-card-caption">{caption}</div>
    </article>
  );
}

const DEFAULT_FILTERS = {
  window: '30d',
  market: 'all',
  state: 'all',
  causeCategory: '',
  customStart: '',
  customEnd: '',
};

function TrendLine({ title, subtitle, points }) {
  const safePoints = Array.isArray(points) ? points : [];
  const values = safePoints
    .map((point) => Number(point?.ratePercent))
    .filter((value) => Number.isFinite(value));

  if (!safePoints.length || !values.length) {
    return (
      <article className="ops-card">
        <div className="ops-card-title">{title}</div>
        <div className="ops-card-caption">{subtitle}</div>
        <div className="ops-card-caption" style={{ marginTop: 12 }}>No trend data in this window.</div>
      </article>
    );
  }

  const width = 320;
  const height = 110;
  const padding = 16;
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue || 1;
  const stepX = safePoints.length > 1 ? (width - padding * 2) / (safePoints.length - 1) : 0;
  const polyline = safePoints.map((point, index) => {
    const value = Number(point?.ratePercent || 0);
    const x = padding + (stepX * index);
    const y = height - padding - (((value - minValue) / range) * (height - padding * 2));
    return `${x},${y}`;
  }).join(' ');

  return (
    <article className="ops-card">
      <div className="ops-card-title">{title}</div>
      <div className="ops-card-caption">{subtitle}</div>
      <div style={{ marginTop: 12 }}>
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="110" role="img" aria-label={title}>
          <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="rgba(22,61,74,0.16)" strokeWidth="1" />
          <polyline
            fill="none"
            stroke="#1f6f78"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={polyline}
          />
          {safePoints.map((point, index) => {
            const value = Number(point?.ratePercent || 0);
            const x = padding + (stepX * index);
            const y = height - padding - (((value - minValue) / range) * (height - padding * 2));
            return <circle key={`${point?.label || index}_${index}`} cx={x} cy={y} r="3.5" fill="#1f6f78" />;
          })}
        </svg>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8, fontSize: 12, color: 'var(--ops-muted, #5d7780)' }}>
        <span>{safePoints[0]?.label || 'Start'}</span>
        <span>{safePoints[safePoints.length - 1]?.label || 'End'}</span>
      </div>
    </article>
  );
}

export default function OpsConsumerMetrics() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  async function loadMetrics(activeFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('window', activeFilters.window || '30d');
      params.set('market', activeFilters.market || 'all');
      params.set('state', activeFilters.state || 'all');
      if (activeFilters.causeCategory) params.set('causeCategory', activeFilters.causeCategory);
      if (activeFilters.window === 'custom') {
        if (activeFilters.customStart) params.set('start', activeFilters.customStart);
        if (activeFilters.customEnd) params.set('end', activeFilters.customEnd);
      }
      const resp = await apiFetch(`/ops/program-health/consumer-metrics?${params.toString()}`);
      setData(resp || null);
    } catch (err) {
      const status = err?.status;
      if (status === 401 || status === 403) {
        clearAuthToken();
        navigate('/login', { replace: true });
        return;
      }
      setData(null);
      setError(err?.data?.error || err?.message || 'Failed to load consumer metrics');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (filters.window === 'custom' && (!filters.customStart || !filters.customEnd)) {
      return;
    }
    if (filters.window === 'custom' && filters.customStart && filters.customEnd) {
      const start = new Date(filters.customStart);
      const end = new Date(filters.customEnd);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
        setLoading(false);
        setError('Custom date range is invalid. Start must be on or before End.');
        return;
      }
    }
    loadMetrics(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const metrics = data?.metrics || {};
  const funnel = metrics.funnel || {};
  const timeToFirstValue = metrics.timeToFirstValue || {};
  const activeUsage = metrics.activeUsage || {};
  const retention = metrics.retention || {};
  const value = metrics.value || {};
  const geographicAssignment = metrics.geographicAssignment || {};
  const activationTrend = Array.isArray(funnel.activationTrend) ? funnel.activationTrend : [];
  const repeatRateTrend = Array.isArray(retention.repeatRateTrend) ? retention.repeatRateTrend : [];
  const assignmentRateTrend = Array.isArray(geographicAssignment.assignmentRateTrend)
    ? geographicAssignment.assignmentRateTrend
    : [];
  const incompleteCount = Math.max(0, Number(funnel.signups || 0) - Number(funnel.linked || 0));

  const funnelCards = [
    {
      title: 'Started',
      value: formatCount(funnel.signups),
      caption: `${formatCount(funnel.linked)} completed • ${formatCount(funnel.activated)} activated • ${formatCount(incompleteCount)} incomplete`,
    },
    {
      title: 'Started → Completed',
      value: formatPercent(funnel.signupToLinkedPercent),
      caption: 'Share of started consumers that completed registration by linking an account',
    },
    {
      title: 'Completed → Activated',
      value: formatPercent(funnel.linkedToActivatedPercent),
      caption: 'Share of completed consumers that reached first eligible transaction',
    },
    {
      title: 'Started → Activated',
      value: formatPercent(funnel.signupToActivatedPercent),
      caption: 'End-to-end conversion from started record to first eligible transaction',
    },
  ];

  const ttfvCards = [
    {
      title: 'Median Time To First Value (Signup)',
      value: formatHours(timeToFirstValue.medianHoursFromSignup),
      caption: 'Median time from signup to first eligible transaction',
    },
    {
      title: 'Median Time To First Value (Link)',
      value: formatHours(timeToFirstValue.medianHoursFromLink),
      caption: 'Median time from first linked account to first eligible transaction',
    },
  ];

  const usageCards = [
    {
      title: 'WAU',
      value: formatCount(activeUsage.wau),
      caption: `${Number(activeUsage.eligibleTransactionsPerActiveConsumer7d || 0).toFixed(2)} eligible tx / active consumer (7d)`,
    },
    {
      title: 'MAU',
      value: formatCount(activeUsage.mau),
      caption: `${Number(activeUsage.eligibleTransactionsPerActiveConsumer30d || 0).toFixed(2)} eligible tx / active consumer (30d)`,
    },
    {
      title: 'Eligible Rate',
      value: formatPercent(activeUsage.eligibleRatePercent),
      caption: `${formatCount(activeUsage.eligibleTransactions)} eligible of ${formatCount(activeUsage.totalObservedTransactions)} observed tx`,
    },
  ];

  const retentionCards = [
    {
      title: 'Repeat Rate (30d)',
      value: formatPercent(retention.repeatRate30Percent),
      caption: `${formatCount(retention.repeat30Count)} of ${formatCount(retention.activatedConsumers)} activated consumers had 2+ eligible tx`,
    },
    {
      title: 'Inactivity Churn (30d)',
      value: formatPercent(retention.inactivityChurn30Percent),
      caption: 'Activated consumers with zero eligible transactions in the last 30 days',
    },
    {
      title: 'Inactivity Churn (45d)',
      value: formatPercent(retention.inactivityChurn45Percent),
      caption: 'Activated consumers with zero eligible transactions in the last 45 days',
    },
  ];

  const valueCards = [
    {
      title: 'Total Eligible GMV',
      value: formatUsdFromCents(value.totalEligibleGmvCents),
      caption: 'Eligible spend in the selected reporting window',
    },
    {
      title: 'Total Contributions',
      value: formatUsdFromCents(value.totalContributionsCents),
      caption: 'Contribution dollars generated in the selected reporting window',
    },
    {
      title: 'Contribution Yield',
      value: value.contributionYieldBps == null ? '—' : `${formatCount(value.contributionYieldBps)} bps`,
      caption: 'Contributions divided by eligible GMV',
    },
  ];

  const geographicAssignmentCards = [
    {
      title: 'Assigned',
      value: formatCount(geographicAssignment?.counts?.assigned),
      caption: 'Consumers with a committed canonical market',
    },
    {
      title: 'Pending Assignment',
      value: formatCount(geographicAssignment?.counts?.pending),
      caption: `${formatCount(geographicAssignment?.counts?.pendingWithCandidate)} with candidate • ${formatCount(geographicAssignment?.counts?.pendingWithoutCandidate)} without candidate`,
    },
    {
      title: 'Unassigned',
      value: formatCount(geographicAssignment?.counts?.unassigned),
      caption: 'Consumers with no committed market and no active assignment candidate',
    },
    {
      title: 'Assigned Rate',
      value: geographicAssignment?.rates?.assignedRate == null ? '—' : formatPercent(Number(geographicAssignment.rates.assignedRate) * 100),
      caption: 'Share of active consumers with a committed market assignment',
    },
  ];

  const assignmentOpsCards = [
    {
      title: 'Median Time to Assignment',
      value: formatDays(geographicAssignment?.timings?.medianTimeToAssignmentDays),
      caption: 'Median time from consumer creation to committed market assignment',
    },
    {
      title: 'Unassigned > 1 Day',
      value: formatCount(geographicAssignment?.aging?.unassignedOver1d),
      caption: 'Unassigned consumers older than one day',
    },
    {
      title: 'Unassigned > 7 Days',
      value: formatCount(geographicAssignment?.aging?.unassignedOver7d),
      caption: 'Unassigned consumers older than seven days',
    },
    {
      title: 'Pending > 7 Days',
      value: formatCount(geographicAssignment?.aging?.pendingOver7d),
      caption: 'Pending consumers older than seven days',
    },
    {
      title: 'Pending > 30 Days',
      value: formatCount(geographicAssignment?.aging?.pendingOver30d),
      caption: 'Pending consumers older than thirty days',
    },
  ];

  return (
    <OpsLayout
      title="Consumer Metrics"
      subtitle="Platform-level consumer funnel, activation, usage, retention, and value metrics"
    >
      <div className="ops-nfp-content">
        <section className="ops-nfp-panel" style={{ marginTop: 0 }}>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Market</span>
              <select
                value={filters.market}
                onChange={(e) => {
                  setError('');
                  setFilters((prev) => ({ ...prev, market: e.target.value }));
                }}
              >
                {(Array.isArray(data?.filters?.markets) ? data.filters.markets : [{ key: 'all', label: 'All Markets' }]).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>State</span>
              <select
                value={filters.state}
                onChange={(e) => {
                  setError('');
                  setFilters((prev) => ({ ...prev, state: e.target.value, market: 'all' }));
                }}
              >
                {(Array.isArray(data?.filters?.states) ? data.filters.states : [{ key: 'all', label: 'All States' }]).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Cause Category</span>
              <select
                value={filters.causeCategory}
                onChange={(e) => {
                  setError('');
                  setFilters((prev) => ({ ...prev, causeCategory: e.target.value }));
                }}
              >
                <option value="">All Categories</option>
                {(Array.isArray(data?.filters?.causeCategories) ? data.filters.causeCategories : []).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Date Range</span>
              <select
                value={filters.window}
                onChange={(e) => {
                  const nextWindow = e.target.value;
                  setFilters((prev) => ({
                    ...prev,
                    window: nextWindow,
                    customStart: nextWindow === 'custom' ? prev.customStart : '',
                    customEnd: nextWindow === 'custom' ? prev.customEnd : '',
                  }));
                }}
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="custom">Custom</option>
              </select>
            </label>

            {filters.window === 'custom' ? (
              <>
                <label className="ops-nfp-field">
                  <span style={{ fontWeight: 600 }}>Start</span>
                  <input
                    type="date"
                    value={filters.customStart}
                    onChange={(e) => {
                      setError('');
                      setFilters((prev) => ({ ...prev, customStart: e.target.value }));
                    }}
                  />
                </label>
                <label className="ops-nfp-field">
                  <span style={{ fontWeight: 600 }}>End</span>
                  <input
                    type="date"
                    value={filters.customEnd}
                    onChange={(e) => {
                      setError('');
                      setFilters((prev) => ({ ...prev, customEnd: e.target.value }));
                    }}
                  />
                </label>
              </>
            ) : null}
          </div>

          {Array.isArray(data?.notes) && data.notes.length ? (
            <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
              {data.notes.map((note) => (
                <div key={note} style={{ fontSize: 12, color: 'var(--ops-muted, #5d7780)' }}>{note}</div>
              ))}
            </div>
          ) : null}
        </section>

        {error ? (
          <section className="ops-nfp-panel">
            <div className="ops-empty-state" style={{ color: '#b74d18' }}>{error}</div>
          </section>
        ) : null}

        {loading ? (
          <section className="ops-nfp-panel">
            <div className="ops-empty-state">Loading consumer metrics…</div>
          </section>
        ) : (
          <>
            <section className="ops-panel">
              <h2>Enrollment + Activation</h2>
              <div className="ops-cards">
                {funnelCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Geographic Assignment Status</h2>
              {geographicAssignment?.scopeNote ? (
                <div style={{ fontSize: 12, color: 'var(--ops-muted, #5d7780)', marginBottom: 12 }}>
                  {geographicAssignment.scopeNote}
                </div>
              ) : null}
              <div className="ops-cards">
                {geographicAssignmentCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Cohort Trends</h2>
              <div className="ops-cards">
                <TrendLine
                  title="Assignment Rate Trend"
                  subtitle="Cumulative committed market assignment across the selected signup cohort"
                  points={assignmentRateTrend}
                />
                <TrendLine
                  title="Activation Rate Trend"
                  subtitle="Cumulative activation of the selected signup cohort across the window"
                  points={activationTrend}
                />
                <TrendLine
                  title="Repeat Rate Trend"
                  subtitle="Rolling 30-day repeat rate across activated consumers in the selected slice"
                  points={repeatRateTrend}
                />
              </div>
            </section>

            <section className="ops-panel">
              <h2>Geographic Assignment Operations</h2>
              <div className="ops-cards">
                {assignmentOpsCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Time To First Value</h2>
              <div className="ops-cards">
                {ttfvCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Active Usage / Participation</h2>
              <div className="ops-cards">
                {usageCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Repeat / Retention</h2>
              <div className="ops-cards">
                {retentionCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Value Generated</h2>
              <div className="ops-cards">
                {valueCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>
          </>
        )}
      </div>
    </OpsLayout>
  );
}
